import type { Body, Meta } from '@uppy/core'
import type { TusOptions } from '@uppy/tus'

import { renewSession } from '@buffer-mono/shared-utils/oidcAuth'

const UNAUTHENTICATED = 401

type TusHooks = TusOptions<Meta, Body>
type TusRequest = Parameters<NonNullable<TusHooks['onBeforeRequest']>>[0]
type TusResponse = Parameters<NonNullable<TusHooks['onAfterResponse']>>[1]

// Declares only the parameters these hooks read, so a narrower one stays
// assignable to the plugin's own signature.
type RenewCredentialsHooks = {
  onBeforeRequest: () => Promise<void>
  onAfterResponse: (request: TusRequest, response: TusResponse) => Promise<void>
  onShouldRetry: NonNullable<TusHooks['onShouldRetry']>
}

let renewal: Promise<boolean> | null = null
let hasFreshCredentials = false

function renewOnce(): Promise<boolean> {
  if (!renewal) {
    renewal = renewSession()
      .then((renewed) => {
        hasFreshCredentials = renewed

        return renewed
      })
      .finally(() => {
        renewal = null
      })
  }

  return renewal
}

/**
 * Recovers an upload the upload service refused as unauthenticated, which an
 * access token expiring mid-upload leaves behind.
 *
 * tus decides whether to retry synchronously, so the renewal happens earlier, in
 * `onAfterResponse` — the one hook tus awaits after a response, including the
 * refusal. By the time `onShouldRetry` is asked, a renewal that worked has
 * rotated the token cookie and the retried request picks it up. Retrying is
 * gated on that: without fresh credentials the repeat would be refused exactly
 * as the first attempt was, so the upload fails with the service's own message
 * rather than spending a second request to learn the same thing.
 *
 * `onBeforeRequest` holds every request back while a renewal is in flight, so
 * the other files in the uploader wait for it rather than spending a request on
 * credentials already known to be stale.
 *
 * The renewal is {@link renewSession}, shared with the rest of the app: an upload
 * and a GraphQL operation refused at the same moment renew once between them.
 */
export const renewCredentialsHooks: RenewCredentialsHooks = {
  onBeforeRequest: async (): Promise<void> => {
    await renewal
  },
  onAfterResponse: async (_request, response): Promise<void> => {
    if (response.getStatus() === UNAUTHENTICATED) {
      await renewOnce()
    }
  },
  onShouldRetry: (error, retryAttempt, _options, next): boolean => {
    // Bounded by the attempt: a second refusal means the renewed token was
    // refused too, so the upload fails with the service's own message.
    if (
      retryAttempt === 0 &&
      hasFreshCredentials &&
      error.originalResponse?.getStatus() === UNAUTHENTICATED
    ) {
      return true
    }

    return next(error)
  },
}
